/**
 * Which `image/*` MIME types a browser can actually paint in an `<img>`.
 *
 * `mime.startsWith('image/')` is the reflex, and it is wrong: it is a statement
 * about the file, not about the renderer. No mainstream browser decodes TIFF,
 * so an `<img src>` pointed at a `.tif` renders the broken-image glyph and its
 * alt text — a dead box where a document should be, with no error and nothing
 * to click. The same holds for a handful of other real-but-undecodable types.
 *
 * Use this wherever the answer decides between `<img>` and a file-card
 * placeholder. Filtering a list of *thumbnails* is the same decision.
 */

/** `image/*` subtypes no mainstream browser decodes in an `<img>` element. */
const UNRENDERABLE_IMAGE_SUBTYPES: readonly string[] = [
  'tiff',
  'x-tiff',
  'tif',
  'vnd.adobe.photoshop',
  'x-photoshop',
  'vnd.dwg',
  'x-eps',
  'x-tga',
  'x-portable-pixmap',
  'x-portable-bitmap',
  'x-portable-graymap',
];

/**
 * True when `mimeType` is an image the browser will actually paint.
 *
 * Returns false for a non-image, for a missing type, and for an image format
 * the browser cannot decode — so a caller can fall through to a download card
 * instead of a broken-image glyph.
 */
export function isBrowserRenderableImage(mimeType: string | null | undefined): boolean {
  if (!mimeType) return false;
  const base = mimeType.split(';', 1)[0].trim().toLowerCase();
  if (!base.startsWith('image/')) return false;
  return !UNRENDERABLE_IMAGE_SUBTYPES.includes(base.slice('image/'.length));
}
